import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import AuthModal from './AuthModal';
import stripeService from '../services/stripeService';

const { FiX, FiCreditCard, FiCheck, FiStar, FiAlertCircle, FiMail, FiUser } = FiIcons;

const PaymentModal = ({ isOpen, onClose, onPaymentSuccess, remainingCalculations, user, onUserUpdate }) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [paymentComplete, setPaymentComplete] = useState(false);
  const [paymentError, setPaymentError] = useState(null);
  const [email, setEmail] = useState('');
  const [isEmailValid, setIsEmailValid] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authMode, setAuthMode] = useState('signup');

  useEffect(() => {
    // Auto-fill email if user is logged in
    if (user?.email) {
      setEmail(user.email);
      setIsEmailValid(true);
    } else {
      setEmail('');
      setIsEmailValid(false);
    }
  }, [user]);

  useEffect(() => {
    // Validate email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    setIsEmailValid(emailRegex.test(email));
  }, [email]);

  // Reset state when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setIsProcessing(false);
      setPaymentComplete(false);
      setPaymentError(null);
    }
  }, [isOpen]);

  const handleStripePayment = async () => {
    // If user is not logged in, show auth modal first
    if (!user) {
      setAuthMode('signup');
      setShowAuthModal(true);
      return;
    }

    try {
      setIsProcessing(true);
      setPaymentError(null);

      console.log('Starting payment process for user:', user.email);

      // Use the hosted checkout page method for reliability
      console.log('Using hosted checkout page');
      const success = stripeService.openCheckoutPage();
      
      if (success) {
        // The user is being redirected to Stripe
        console.log('User redirected to Stripe checkout');
        // Don't set processing to false here as the user is leaving the page
      } else {
        throw new Error('Failed to redirect to payment page');
      }

    } catch (error) {
      console.error('Payment error:', error);
      setIsProcessing(false);
      setPaymentError(`Payment failed: ${error.message}. Please try again.`);
    }
  };

  const handleAuthSuccess = (userData) => {
    setShowAuthModal(false);
    if (onUserUpdate) {
      onUserUpdate(userData);
    }
    // Email will be auto-filled from the new user data
  };

  // This function is called from the parent component when payment is verified
  const handlePaymentSuccess = () => {
    setIsProcessing(false);
    setPaymentComplete(true);
    
    // Auto-close and trigger success after showing confirmation
    setTimeout(() => {
      onClose();
    }, 2000);
  };

  // Call this when parent component confirms payment
  useEffect(() => {
    if (paymentComplete) {
      onPaymentSuccess();
    }
  }, [paymentComplete, onPaymentSuccess]);

  if (!isOpen) return null;

  return (
    <>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          className="bg-white rounded-2xl shadow-xl w-full max-w-md"
          onClick={(e) => e.stopPropagation()}
        >
          {paymentComplete ? (
            // Success State
            <div className="p-6 text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <SafeIcon icon={FiCheck} className="text-2xl text-green-600" />
              </div>
              <h3 className="text-2xl font-bold text-gray-800 mb-2">Payment Successful!</h3>
              <p className="text-gray-600 mb-4">
                Thank you for upgrading! You now have unlimited access to the meal calculator.
              </p>
              <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                <p className="text-green-800 font-semibold">🎉 Unlimited meal calculations unlocked!</p>
              </div>
            </div>
          ) : (
            // Payment State
            <>
              <div className="p-6 border-b border-gray-200 flex justify-between items-center">
                <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
                  <SafeIcon icon={FiStar} className="text-yellow-500" />
                  Upgrade to Pro
                </h2>
                <button
                  onClick={onClose}
                  className="text-gray-500 hover:text-gray-700 p-1 rounded-full hover:bg-gray-100"
                >
                  <SafeIcon icon={FiX} className="text-xl" />
                </button>
              </div>

              <div className="p-6 space-y-6">
                {/* Usage Info */}
                <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                  <h3 className="font-semibold text-orange-800 mb-2">Free Trial Used Up!</h3>
                  <p className="text-orange-700 text-sm">
                    You've successfully completed {3 - remainingCalculations} optimized meal calculations within your target thresholds. To continue using the meal calculator, please upgrade to Pro.
                  </p>
                </div>

                {/* User Status */}
                {user ? (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <SafeIcon icon={FiUser} className="text-blue-600" />
                      <span className="font-medium text-blue-800">Signed in as {user.firstName} {user.lastName}</span>
                    </div>
                    <p className="text-blue-700 text-sm">
                      Your subscription will be linked to {user.email}
                    </p>
                  </div>
                ) : (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                    <h3 className="font-semibold text-yellow-800 mb-2">Create Account Required</h3>
                    <p className="text-yellow-700 text-sm">
                      Please create an account to manage your subscription and access premium features.
                    </p>
                  </div>
                )}

                {/* Pro Features */}
                <div>
                  <h3 className="font-semibold text-gray-800 mb-3">Pro Features:</h3>
                  <div className="space-y-2">
                    {[
                      'Unlimited meal calculations',
                      'Access to FatSecret food database (500,000+ foods)',
                      'Advanced macro optimization',
                      'Custom food creation',
                      'Export meal plans',
                      'Priority support'
                    ].map((feature, index) => (
                      <div key={index} className="flex items-center gap-2">
                        <SafeIcon icon={FiCheck} className="text-green-600" />
                        <span className="text-gray-700">{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Pricing */}
                <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4 text-center">
                  <div className="text-3xl font-bold text-indigo-600 mb-1">$9.99 / month</div>
                  <div className="text-indigo-700 font-medium">Unlock Your Best Meals</div>
                  <div className="text-indigo-600 text-sm">Unlimited Premium Features • Cancel Anytime</div>
                </div>

                {/* Email Input (only if not logged in) */}
                {!user && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                      <SafeIcon icon={FiMail} className="text-gray-500" />
                      Email Address (for payment receipt)
                    </label>
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="your@email.com"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    />
                  </div>
                )}

                {/* Error Message */}
                {paymentError && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-center gap-2 text-red-700">
                    <SafeIcon icon={FiAlertCircle} className="text-red-500 flex-shrink-0" />
                    <span className="text-sm">{paymentError}</span>
                  </div>
                )}

                {/* Payment Button */}
                <button
                  onClick={handleStripePayment}
                  disabled={isProcessing || (!user && email && !isEmailValid)}
                  className="w-full bg-indigo-600 hover:bg-indigo-700 disabled:bg-indigo-400 text-white py-4 px-6 rounded-lg font-semibold text-lg transition-all duration-300 flex items-center justify-center gap-3"
                >
                  {isProcessing ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent" />
                      <span>Redirecting to Stripe...</span>
                    </>
                  ) : !user ? (
                    <>
                      <SafeIcon icon={FiUser} className="text-xl" />
                      <span>Create Account & Pay</span>
                    </>
                  ) : (
                    <>
                      <SafeIcon icon={FiCreditCard} className="text-xl" />
                      <span>Pay with Stripe</span>
                    </>
                  )}
                </button>

                <div className="text-center text-xs text-gray-500">
                  Secure payment powered by Stripe • 30-day money-back guarantee
                </div>
              </div>
            </>
          )}
        </motion.div>
      </motion.div>

      {/* Auth Modal */}
      <AuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        mode={authMode}
        onSuccess={handleAuthSuccess}
        onModeChange={setAuthMode}
      />
    </>
  );
};

export default PaymentModal;